import { useEffect, useState } from 'react';
import { Projects, type TicketProjectLink } from '../lib/api';

const PROJECTS_BASE = 'https://j1l-projects.web.app';

function projectUrl(l: TicketProjectLink) {
  return `${PROJECTS_BASE}/projects/${l.project.id}`;
}
function featureUrl(l: TicketProjectLink) {
  return l.feature ? `${PROJECTS_BASE}/projects/${l.project.id}/features/${l.feature.id}` : null;
}

/** Opens j1l-projects in a new tab. Same-tab navigation would discard an
 * unposted draft in the always-available comment composer on this page, with no
 * warning — so every crumb is an external link, marked as one. */
function Crumb({ href, label, value }: { href: string | null; label: string; value: string }) {
  const cls =
    'max-w-[16rem] truncate text-slate-700 hover:text-brand-600 underline decoration-slate-300 underline-offset-2';
  if (!href) return <span className={cls} title={value}>{value}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cls}
      title={value}
      aria-label={`${label}: ${value} (opens j1l-projects in a new tab)`}
    >
      {value}
      <span aria-hidden="true" className="ml-0.5 text-[0.65rem] align-super">↗</span>
    </a>
  );
}

/**
 * "Project → Feature → Task" strip showing where a ticket lives in the projects
 * app. Renders one row per link — a ticket can be attached to more than one
 * project, and the chain can stop at any level.
 *
 * Renders NOTHING on every failure and on the empty case. That is deliberate:
 * most tickets are standalone, most ticketing users have no projects access, and
 * an error banner on the most-visited page in the app would be noise. The
 * trade-off is that a genuine outage is invisible, so each case is logged
 * distinctly to the console for whoever is debugging "why is there no
 * breadcrumb".
 */
export function ProjectBreadcrumb({ ticketId }: { ticketId: number }) {
  const [links, setLinks] = useState<TicketProjectLink[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Fire-and-forget: never block or delay the ticket render behind this.
    Projects.linksForTicket(ticketId)
      .then((r) => {
        if (!cancelled) setLinks(r.links);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as { status?: number } | null)?.status;
        // Note there is no 403 case: the endpoint answers {links:[]} rather than
        // 403 for users without projects access, precisely so this stays quiet.
        // A 401 means the session expired — the rest of the page handles that.
        if (status === 404) {
          console.info('[project-links] 404 — projects plugin not mounted on this API');
        } else {
          console.warn(`[project-links] lookup failed (status ${status ?? 'n/a'})`, e);
        }
        setLinks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  if (!links || links.length === 0) return null;

  return (
    <nav
      aria-label={links.length > 1 ? 'Linked projects' : 'Linked project'}
      className="mt-2 flex flex-col gap-1"
    >
      {links.map((l) => (
        <div key={l.id} className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="text-slate-500">in</span>
          <Crumb href={projectUrl(l)} label="Project" value={l.project.name} />
          {l.feature && (
            <>
              <span aria-hidden="true" className="text-slate-400">›</span>
              <Crumb href={featureUrl(l)} label="Feature" value={l.feature.title} />
            </>
          )}
          {l.task && (
            <>
              <span aria-hidden="true" className="text-slate-400">›</span>
              {/* Tasks have no page of their own — they live on the feature. */}
              <Crumb href={null} label="Task" value={l.task.title} />
            </>
          )}
        </div>
      ))}
    </nav>
  );
}
